/**
 * @file init-queue-mixin.ts
 *
 * Manages the post-configurator-init task queue. Operations enqueued before
 * the configurator is ready are held and executed sequentially once
 * initialization completes.
 *
 * Also locks as-step-selector behind an as-app-busy event while the
 * configurator is initializing, releasing it when the queue drains.
 *
 * External event bridge: listens for any known external configurator-ready
 * signal (configurator-initialized, paramate-configurator-initialized) and
 * normalises them into a single internal 'as-intrn-configurator-ready' event.
 * All framework components should listen to the internal event only; only
 * this bridge needs updating when the external library changes its event name.
 *
 * Exposes: isInitialized, addToPostInitQueue
 */
import { type Constructor } from './mixin-types-utils.ts';

/** Surface added by InitQueueMixin. Referenced by ProjectMixin and MeshMixin
 *  which enqueue operations that must run after configurator init. */
export interface InitQueueMixinMembers {
  isInitialized: boolean;
  /** Runs operation immediately if already initialized; queues it otherwise. */
  addToPostInitQueue(operation: () => void): void;
}

export const InitQueueMixin = <T extends Constructor<HTMLElement>>(Base: T) =>
  class InitQueueMixin extends Base {
    isInitialized: boolean = false;
    postInitQueue: Array<() => void> = [];
    _stepSelectorLockedForInit = false;
    private _configReadyEmitted = false;

    private _handleExternalConfiguratorInit = (ev: Event): void => {
      if (this._configReadyEmitted) return;
      this._configReadyEmitted = true;
      window.dispatchEvent(new CustomEvent('as-intrn-configurator-ready', {
        detail: (ev as CustomEvent).detail,
      }));
    };

    private _handleConfiguratorInitialized = async (): Promise<void> => {
      this.isInitialized = true;
      await this._execPostInitQueue();
      if (this._stepSelectorLockedForInit) {
        this._stepSelectorLockedForInit = false;
        window.dispatchEvent(new CustomEvent('as-app-busy-request', { detail: { key: 'init', busy: false } }));
      }
    };

    public addToPostInitQueue(operation: () => void) {
      if (this.isInitialized) {
        operation();
      } else {
        this.postInitQueue.push(operation);
      }
    }

    private _execPostInitQueue = async (): Promise<void> => {
      while (this.postInitQueue.length > 0) {
        const operation = this.postInitQueue.shift();
        if (operation) await operation();
      }
    };

    async connectedCallback() {
      await super.connectedCallback?.();
      window.addEventListener('configurator-initialized', this._handleExternalConfiguratorInit as EventListener);
      window.addEventListener('paramate-configurator-initialized', this._handleExternalConfiguratorInit as EventListener);
      window.addEventListener('as-intrn-configurator-ready', this._handleConfiguratorInitialized as EventListener);

      // Delay initial lock to ensure BusyMixin and StepSelector are ready to receive it
      setTimeout(() => {
        if (this.querySelector('as-step-selector') && this.querySelector('as-configurator')) {
          this._stepSelectorLockedForInit = true;
          window.dispatchEvent(new CustomEvent('as-app-busy-request', { detail: { key: 'init', busy: true } }));
        }
      }, 200);
    }

    disconnectedCallback() {
      window.removeEventListener('configurator-initialized', this._handleExternalConfiguratorInit as EventListener);
      window.removeEventListener('paramate-configurator-initialized', this._handleExternalConfiguratorInit as EventListener);
      window.removeEventListener('as-intrn-configurator-ready', this._handleConfiguratorInitialized as EventListener);
      super.disconnectedCallback?.();
    }
  };
